import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { some } from 'lodash';
import isNil from 'lodash/isNil';
import React, { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import {
  DataDateRange,
  DataFrequency,
  EditableInvestmentName,
  EditablePortfolioName,
  FlexGroup,
  getCreatedDisplay,
  getUpdatedDisplay,
  InvestmentDownloadButton,
  ManageDataTitle,
  PlaceholderWrapper,
  PortfolioDownloadButton,
  PrivateFundEditableName,
  Summary,
  SummaryBaseWrapper,
  SummaryItem,
  SummaryPlaceholder,
  SummaryWrapper,
  type UseMetaDataReturn,
  UserContext,
  type UserData,
} from 'venn-components';
import {
  GetColor,
  HistoricalPortfolioIcon,
  Icon,
  Label,
  PrivateInvestmentIcon,
  PrivatePortfolioIcon,
  type Theme,
} from 'venn-ui-kit';
import { type AnalysisSubject, FundUtils, isHistoricalPortfolio } from 'venn-utils';
import {
  isRangeAnalysisBlockingHistoricalError,
  isHistoricalError,
} from '../../../../venn-components/src/bulk-management/utils';
import { ManageDataHeaderHistoricalErrorBanner } from './ManageDataHeaderHistoricalErrorBanner';
import { useRangeAnalysis } from 'venn-components';

interface ManageDataHeaderProps {
  subject?: AnalysisSubject;
  investmentUserData?: UserData;
  investmentUseMetaDataReturn?: UseMetaDataReturn;
  onNameUpdated?: (name: string) => void;
}

export default function ManageDataHeader({
  subject,
  onNameUpdated,
  investmentUserData,
  investmentUseMetaDataReturn,
}: ManageDataHeaderProps) {
  const { hasPermission, hasPermissionForResource } = useContext(UserContext);
  const { Colors } = useContext(ThemeContext);

  const { primaryAnalysis, primaryRangeAnalysis } = useRangeAnalysis(subject);

  const primarySubjectHasHistoricalError =
    subject?.portfolio?.historical &&
    !isNil(primaryAnalysis) &&
    some(primaryAnalysis.rangeAnalyses, (rangeAnalysis) => isHistoricalError(rangeAnalysis.historicalError));
  const hasRangeAnalysisBlockingError =
    primarySubjectHasHistoricalError &&
    some(primaryAnalysis.rangeAnalyses, (rangeAnalysis) =>
      isRangeAnalysisBlockingHistoricalError(rangeAnalysis.historicalError),
    );
  if (!subject) {
    return (
      <>
        <PlaceholderWrapper>
          <SummaryPlaceholder duration={3} />
        </PlaceholderWrapper>
        <SummaryBaseWrapper refreshedStyling>
          <SummaryPlaceholder duration={3} />
          <SummaryPlaceholder duration={3} />
        </SummaryBaseWrapper>
      </>
    );
  }

  const icon = (() => {
    if (subject.portfolio) {
      if (isHistoricalPortfolio(subject.portfolio)) {
        return <HistoricalPortfolioIcon color={Colors.DarkBlue} className="mr-[8px] !h-[2.2em]" />;
      }
      return <StyledIcon type="folder" colorGetter={GetColor.DarkBlue} />;
    }
    if (subject?.privatePortfolio) {
      return <PrivatePortfolioIcon className={cn('mr-[8px] !h-[2.2em]')} />;
    }
    if (subject?.privateFund) {
      return <PrivateInvestmentIcon className={cn('mr-[8px] !h-[2.2em]')} />;
    }
    if (subject?.fund?.assetType === 'BENCHMARK') {
      return <StyledIcon type="layer-group" colorGetter={GetColor.DEPRECATED_DataLineColor.Pink} />;
    }
    return <StyledIcon type="square" colorGetter={GetColor.DEPRECATED_DataLineColor.Gold} />;
  })();

  const name = subject.portfolio ? (
    <EditablePortfolioName
      portfolio={subject.portfolio}
      onChange={onNameUpdated}
      disabled={!hasPermissionForResource('EDIT_PORTFOLIO', subject.portfolio)}
    />
  ) : subject.fund && investmentUseMetaDataReturn ? (
    <EditableInvestmentName
      useMetaDataReturn={investmentUseMetaDataReturn}
      disabled={!subject.fund.userEditable || !hasPermission('UPLOAD_RETURNS')}
      onChange={onNameUpdated}
    />
  ) : subject.privateFund ? (
    <PrivateFundEditableName
      onChange={onNameUpdated}
      disabled={!subject.privateFund.userUploaded}
      privateFund={subject.privateFund}
    />
  ) : subject.privatePortfolio ? (
    <EditablePortfolioName portfolio={subject.privatePortfolio} onChange={onNameUpdated} isPrivate />
  ) : null;

  return (
    <>
      <SummaryBaseWrapper refreshedStyling>
        <FlexGroup>
          <ManageDataTitle refreshedStyling>
            {icon}
            {name}
          </ManageDataTitle>
        </FlexGroup>
        <FlexGroup>
          {subject.portfolio && !subject.portfolio.historical ? (
            <PortfolioDownloadButton portfolio={subject.portfolio} />
          ) : null}
          {investmentUserData && FundUtils.isPerformanceViewable(subject?.fund) ? (
            <InvestmentDownloadButton subjectId={subject.id} userData={investmentUserData} absolutePosition={false} />
          ) : null}
        </FlexGroup>
      </SummaryBaseWrapper>
      <SummaryWrapper className="qa-header-summary" refreshedStyling>
        {!subject.private && (
          <FlexGroup>
            <Summary>
              <SummaryItem>
                <div className="flex flex-row gap-[3px]">
                  <Label>{subject.portfolio?.historical ? 'Available Analysis Period' : 'Available Dates'}</Label>
                  <AvailableDataInfoTooltip isHistorical={!!subject.portfolio?.historical} />
                </div>
                {primarySubjectHasHistoricalError && hasRangeAnalysisBlockingError ? (
                  'No Available Analysis Period'
                ) : (
                  <DataDateRange dataRangeInfo={primaryRangeAnalysis} />
                )}
              </SummaryItem>
              <SummaryItem>
                <div className="flex flex-row gap-[3px]">
                  <Label>Available Frequency</Label>
                  <AvailableDataInfoTooltip isHistorical={!!subject.portfolio?.historical} />
                </div>
                <DataFrequency dataRangeInfo={primaryRangeAnalysis} />
              </SummaryItem>
            </Summary>
          </FlexGroup>
        )}
        <FlexGroup>
          <SummaryItem>
            <Label>Created:</Label>
            <>{getCreatedDisplay(subject.item)}</>
          </SummaryItem>
          <SummaryItem>
            <Label>Last Updated:</Label>
            <>{getUpdatedDisplay(subject.item)}</>
          </SummaryItem>
        </FlexGroup>
      </SummaryWrapper>
      {primarySubjectHasHistoricalError && (
        <ManageDataHeaderHistoricalErrorBanner>
          {hasRangeAnalysisBlockingError
            ? 'Portfolio cannot be analyzed. Please resolve errors by uploading missing data or adding a proxy.'
            : 'The available analysis period is constrained. Please review below and upload missing data or add a proxy to resolve constraints.'}
        </ManageDataHeaderHistoricalErrorBanner>
      )}
    </>
  );
}

const StyledIcon = styled(Icon)<{ colorGetter: (props: { theme: Theme }) => string }>`
  color: ${(props) => props.colorGetter};
  margin-right: 8px;
`;

function AvailableDataInfoTooltip({ isHistorical }: Readonly<{ isHistorical: boolean }>) {
  const content = isHistorical
    ? 'Based on returns data, allocations, and any applied proxies.'
    : 'Based on available data of subject with any proxies applied.';

  return (
    <Tooltip>
      <TooltipTrigger>
        <Icon className="text-venn-grey-80 hover:text-venn-dark-blue" prefix="far" type="circle-info" />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContentWithArrow>{content}</TooltipContentWithArrow>
      </TooltipPortal>
    </Tooltip>
  );
}
